import { useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { ArrowLeft, Plus, X, GitCompareArrows } from "lucide-react";
import {
  CartesianGrid,
  Legend,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import { api, cls, fmtAum, fmtNav, fmtPct } from "@/lib/api";

const SERIES_COLORS = ["#0B3C5D", "#14B8A6", "#F59E0B", "#8B5CF6"];

const RETURN_PERIODS = ["1W", "1M", "3M", "6M", "YTD", "1Y", "SI"];

export default function Compare() {
  const [searchParams, setSearchParams] = useSearchParams();
  const initialCodes = (searchParams.get("codes") || "").split(",").filter(Boolean);
  const [allFunds, setAllFunds] = useState([]);
  const [selectedCodes, setSelectedCodes] = useState(initialCodes);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [pickerOpen, setPickerOpen] = useState(false);
  const [q, setQ] = useState("");

  useEffect(() => {
    api.get("/funds").then((r) => setAllFunds(r.data));
  }, []);

  useEffect(() => {
    if (selectedCodes.length < 2) {
      setData(null);
      return;
    }
    setLoading(true);
    api
      .get("/compare", { params: { codes: selectedCodes.join(",") } })
      .then((r) => setData(r.data))
      .finally(() => setLoading(false));
    setSearchParams({ codes: selectedCodes.join(",") });
  }, [selectedCodes, setSearchParams]);

  const availableToAdd = useMemo(
    () =>
      allFunds
        .filter((f) => !selectedCodes.includes(f.code))
        .filter((f) => {
          if (!q) return true;
          const s = q.toLowerCase();
          return f.name.toLowerCase().includes(s) || f.amc.toLowerCase().includes(s);
        })
        .slice(0, 40),
    [allFunds, selectedCodes, q]
  );

  const chartData = useMemo(() => {
    if (!data) return [];
    // merge rebased_nav from each fund on date
    const map = new Map();
    data.funds.forEach((f) => {
      f.rebased_nav.forEach((p) => {
        if (!map.has(p.date)) map.set(p.date, { date: p.date });
        map.get(p.date)[f.code] = p.value;
      });
    });
    return Array.from(map.values()).sort((a, b) => a.date.localeCompare(b.date));
  }, [data]);

  const addFund = (code) => {
    if (selectedCodes.length >= 4) return;
    setSelectedCodes([...selectedCodes, code]);
    setPickerOpen(false);
    setQ("");
  };

  const removeFund = (code) => setSelectedCodes(selectedCodes.filter((c) => c !== code));

  return (
    <div className="max-w-7xl mx-auto px-6 py-8">
      <Link
        to="/"
        data-testid="compare-back-link"
        className="inline-flex items-center gap-1.5 text-sm text-sif-muted hover:text-sif-primary transition-colors mb-5"
      >
        <ArrowLeft className="w-4 h-4" /> Back to Funds
      </Link>

      <div className="mb-6">
        <div className="flex items-center gap-2 text-xs font-semibold text-sif-secondary uppercase tracking-widest">
          <GitCompareArrows className="w-3.5 h-3.5" /> Peer Comparison
        </div>
        <h1 className="mt-2 text-3xl font-bold text-sif-primary">Compare up to 4 SIFs side-by-side</h1>
        <p className="mt-2 text-sm text-sif-muted">
          Rebased NAV chart (base = 100), rolling returns matrix, and cost/fee summary.
        </p>
      </div>

      {/* Selected chips */}
      <div
        data-testid="compare-selection"
        className="bg-white border border-sif rounded-xl p-4 card-shadow mb-6"
      >
        <div className="flex flex-wrap items-center gap-2">
          {selectedCodes.length === 0 && (
            <span className="text-sm text-sif-muted">Add at least 2 funds to compare…</span>
          )}
          {selectedCodes.map((code, idx) => {
            const f = allFunds.find((x) => x.code === code);
            return (
              <div
                key={code}
                data-testid={`compare-chip-${code}`}
                className="inline-flex items-center gap-2 pl-3 pr-2 py-1.5 rounded-full text-sm font-medium text-white"
                style={{ backgroundColor: SERIES_COLORS[idx] }}
              >
                <span>{f?.name.split("Long-Short")[0].trim() || code}</span>
                <button
                  onClick={() => removeFund(code)}
                  data-testid={`compare-remove-${code}`}
                  className="p-0.5 rounded-full hover:bg-white/20"
                >
                  <X className="w-3.5 h-3.5" />
                </button>
              </div>
            );
          })}
          {selectedCodes.length < 4 && (
            <div className="relative">
              <button
                data-testid="compare-add-btn"
                onClick={() => setPickerOpen((v) => !v)}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm font-medium border border-dashed border-sif-primary/30 text-sif-primary hover:bg-sif-primary/5"
              >
                <Plus className="w-3.5 h-3.5" /> Add fund
              </button>
              {pickerOpen && (
                <div
                  data-testid="fund-picker"
                  className="absolute z-30 top-full left-0 mt-2 w-[380px] bg-white border border-sif rounded-xl card-shadow-lg p-2"
                >
                  <input
                    autoFocus
                    value={q}
                    onChange={(e) => setQ(e.target.value)}
                    placeholder="Search fund or AMC…"
                    className="w-full px-3 py-2 rounded-md border border-sif bg-slate-50 text-sm focus:outline-none focus:ring-2 focus:ring-sif-primary/20 mb-1"
                  />
                  <div className="max-h-72 overflow-y-auto">
                    {availableToAdd.map((f) => (
                      <button
                        key={f.code}
                        data-testid={`picker-option-${f.code}`}
                        onClick={() => addFund(f.code)}
                        className="w-full text-left px-3 py-2 hover:bg-slate-50 rounded-md"
                      >
                        <div className="text-sm font-medium text-sif-primary">{f.name}</div>
                        <div className="text-xs text-sif-muted">{f.amc} · {f.category}</div>
                      </button>
                    ))}
                    {availableToAdd.length === 0 && (
                      <div className="text-xs text-sif-muted px-3 py-4 text-center">No matches</div>
                    )}
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {selectedCodes.length < 2 && (
        <div className="bg-white border border-dashed border-sif rounded-xl p-16 text-center">
          <GitCompareArrows className="w-10 h-10 mx-auto text-sif-muted opacity-40 mb-3" />
          <div className="text-sif-muted text-sm">Pick at least 2 funds to see the comparison</div>
        </div>
      )}

      {loading && (
        <div className="text-center py-12 text-sif-muted text-sm">Crunching comparison…</div>
      )}

      {data && !loading && (
        <>
          {/* Rebased chart */}
          <section
            data-testid="rebased-chart-section"
            className="bg-white border border-sif rounded-xl p-6 card-shadow mb-6"
          >
            <div className="mb-4">
              <div className="text-xs text-sif-muted uppercase tracking-wider font-semibold">Rebased NAV</div>
              <div className="text-lg font-semibold text-sif-primary mt-0.5">
                Growth of ₹100 since inception
              </div>
            </div>
            <div className="h-[360px]">
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={chartData}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" vertical={false} />
                  <XAxis
                    dataKey="date"
                    tick={{ fontSize: 11, fill: "#64748B" }}
                    stroke="#CBD5E1"
                    minTickGap={40}
                  />
                  <YAxis
                    tick={{ fontSize: 11, fill: "#64748B" }}
                    stroke="#CBD5E1"
                    domain={["dataMin - 2", "dataMax + 2"]}
                  />
                  <Tooltip
                    contentStyle={{
                      background: "white",
                      border: "1px solid #E2E8F0",
                      borderRadius: "8px",
                      fontSize: "12px",
                    }}
                    formatter={(v) => v?.toFixed(2)}
                  />
                  <Legend
                    wrapperStyle={{ fontSize: "12px", paddingTop: "8px" }}
                    formatter={(code) => {
                      const f = data.funds.find((x) => x.code === code);
                      return f?.name.split("Long-Short")[0].trim() || code;
                    }}
                  />
                  {data.funds.map((f, idx) => (
                    <Line
                      key={f.code}
                      type="monotone"
                      dataKey={f.code}
                      stroke={SERIES_COLORS[idx]}
                      strokeWidth={2}
                      dot={false}
                      connectNulls
                    />
                  ))}
                </LineChart>
              </ResponsiveContainer>
            </div>
          </section>

          {/* Rolling returns matrix */}
          <section
            data-testid="returns-matrix-section"
            className="bg-white border border-sif rounded-xl card-shadow mb-6 overflow-hidden"
          >
            <div className="p-6 pb-3">
              <div className="text-xs text-sif-muted uppercase tracking-wider font-semibold">Rolling Returns Matrix</div>
              <div className="text-lg font-semibold text-sif-primary mt-0.5">Period-wise performance</div>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-slate-50">
                  <tr className="text-[11px] uppercase tracking-wider text-sif-muted">
                    <th className="px-5 py-3 text-left font-semibold">Fund</th>
                    {RETURN_PERIODS.map((p) => (
                      <th key={p} className="px-5 py-3 text-right font-semibold">{p}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {data.funds.map((f, idx) => (
                    <tr key={f.code} className="border-t border-sif">
                      <td className="px-5 py-4">
                        <div className="flex items-center gap-2">
                          <span
                            className="w-2.5 h-2.5 rounded-full"
                            style={{ backgroundColor: SERIES_COLORS[idx] }}
                          />
                          <div>
                            <div className="font-semibold text-sif-primary text-sm">
                              {f.name.split("Long-Short")[0].trim()}
                            </div>
                            <div className="text-xs text-sif-muted">{f.amc}</div>
                          </div>
                        </div>
                      </td>
                      {RETURN_PERIODS.map((p) => (
                        <td
                          key={p}
                          className={`px-5 py-4 text-right font-mono text-sm ${cls(f.rolling_returns[p])}`}
                        >
                          {fmtPct(f.rolling_returns[p])}
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </section>

          {/* Cost & structure */}
          <section
            data-testid="cost-matrix-section"
            className="bg-white border border-sif rounded-xl card-shadow overflow-hidden"
          >
            <div className="p-6 pb-3">
              <div className="text-xs text-sif-muted uppercase tracking-wider font-semibold">Cost & Structure</div>
              <div className="text-lg font-semibold text-sif-primary mt-0.5">Fees, benchmark & basics</div>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-slate-50">
                  <tr className="text-[11px] uppercase tracking-wider text-sif-muted">
                    <th className="px-5 py-3 text-left font-semibold">Metric</th>
                    {data.funds.map((f, idx) => (
                      <th key={f.code} className="px-5 py-3 text-right font-semibold">
                        <span className="inline-flex items-center gap-1.5">
                          <span
                            className="w-2 h-2 rounded-full"
                            style={{ backgroundColor: SERIES_COLORS[idx] }}
                          />
                          {f.name.split("Long-Short")[0].trim()}
                        </span>
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody className="text-sm">
                  <CompareRow label="Latest NAV" values={data.funds.map((f) => fmtNav(f.latest_nav))} mono />
                  <CompareRow label="1D Change" values={data.funds.map((f) => ({ text: fmtPct(f.day_change_pct), cls: cls(f.day_change_pct) }))} />
                  <CompareRow label="AUM" values={data.funds.map((f) => fmtAum(f.aum_cr))} />
                  <CompareRow label="Expense Ratio" values={data.funds.map((f) => `${f.ter}%`)} />
                  <CompareRow label="Category" values={data.funds.map((f) => f.category)} />
                  <CompareRow label="Benchmark" values={data.funds.map((f) => f.benchmark)} />
                  <CompareRow label="Min Investment" values={data.funds.map((f) => `₹${f.min_investment.toLocaleString("en-IN")}`)} />
                  <CompareRow label="Launch Date" values={data.funds.map((f) => f.launch_date?.slice(0, 10))} />
                  <CompareRow label="Data Source" values={data.funds.map((f) => (
                    <span className={`inline-block px-2 py-0.5 rounded text-[10px] font-semibold uppercase ${f.data_source === "live" ? "bg-sif-secondary/15 text-sif-secondary" : "bg-slate-100 text-sif-muted"}`}>
                      {f.data_source}
                    </span>
                  ))} />
                </tbody>
              </table>
            </div>
          </section>
        </>
      )}
    </div>
  );
}

function CompareRow({ label, values, mono }) {
  return (
    <tr className="border-t border-sif">
      <td className="px-5 py-3 text-sif-muted font-medium">{label}</td>
      {values.map((v, i) => {
        const isObj = v && typeof v === "object" && "text" in v;
        return (
          <td
            key={i}
            className={`px-5 py-3 text-right ${mono ? "font-mono" : ""} ${isObj ? v.cls : "text-sif-primary font-semibold"}`}
          >
            {isObj ? v.text : v}
          </td>
        );
      })}
    </tr>
  );
}
